import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { formatCurrency } from ".";

interface CustomerByCountry {
  country: string;
  count: number;
  percentage: number;
}

interface TopCustomer {
  id: string;
  name: string;
  totalSpent: number;
  tripsCount: number;
}

interface CustomerGrowth {
  month: string;
  newCustomers: number;
}

interface CustomerStatsProps {
  byCountry: CustomerByCountry[];
  topCustomers: TopCustomer[];
  avgLifetimeValue: number;
  newVsReturning: {
    new: number;
    returning: number;
  };
  growthByMonth: CustomerGrowth[];
}

const COUNTRY_NAMES: Record<string, string> = {
  CZ: "Czech Republic",
  SK: "Slovakia",
  DE: "Germany",
  AT: "Austria",
  PL: "Poland",
  HU: "Hungary",
  GB: "United Kingdom",
  US: "USA",
};

const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#553dad",
  "#14b8a6",
  "#f97316",
];

export default function CustomerStats({
  byCountry,
  topCustomers,
  avgLifetimeValue,
  newVsReturning,
  growthByMonth: _growthByMonth,
}: CustomerStatsProps) {
  const countryData = byCountry.slice(0, 8).map((c, idx) => ({
    name: COUNTRY_NAMES[c.country] || c.country,
    value: c.count,
    percentage: c.percentage,
    color: COLORS[idx % COLORS.length],
  }));

  const newReturningData = [
    { name: "New", value: newVsReturning.new, color: "#3b82f6" },
    { name: "Returning", value: newVsReturning.returning, color: "#22c55e" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Countries Pie Chart */}
        <div className="card lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Customers by country
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {countryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, _, props) => [
                    `${value} (${props.payload.percentage}%)`,
                    props.payload.name,
                  ]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Legend
                  formatter={(value, _entry) => <span style={{ color: "#374151" }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* New vs Returning */}
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            New vs returning
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={newReturningData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {newReturningData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value}%`, ""]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-blue-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                New {newVsReturning.new}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Returning {newVsReturning.returning}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* CLV Card */}
        <div className="card flex flex-col items-center justify-center py-8">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Average customer lifetime value (CLV)
          </p>
          <p className="text-primary-600 dark:text-primary-400 mt-2 text-4xl font-bold">
            {formatCurrency(avgLifetimeValue)}
          </p>
        </div>

        {/* Top Customers Table */}
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Top 10 customers
          </h3>
          <div className="max-h-64 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="sticky top-0 bg-white dark:bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Customer
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                    Trips
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Spent
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {topCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="whitespace-nowrap px-3 py-2">
                      <Link
                        to={`/customers/${customer.id}`}
                        className="text-primary-600 font-medium hover:underline"
                      >
                        {customer.name || "–"}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        {customer.tripsCount}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(customer.totalSpent)}
                    </td>
                  </tr>
                ))}
                {topCustomers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-gray-500">
                      No customers
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
